// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSims from '../../../api/actionsSims';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.setLaunchRefetch
 * @returns {JSX.Element} ModalResetPassword Component
 */
function CustomPayModalAddSimCards({ isOpen, setIsOpen, setLaunchRefetch }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.add.steps';

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);

    // Zod Schema
    const formSchema = z.object({
        orderId: z.string().trim().min(1)
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { orderId: '' }
    });

    // API Calls add SimCard
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity={'modal.add.success'} namespace={nsSimCard} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.add.error'} namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitAddSimCardsFromOrderId = actionsSims.addSimCardsFromOrderId(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.order`, disabledNext: !form.formState.isValid },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.confirmation`, disabledNext: false }
        }),
        [form.formState.isValid]
    );

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn('max-w-lg')}>
                <HeaderTitle title={'modal.add.title'} namespace={nsSimCard} />
                <ModalBody>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSimCard}
                        className='mb-2 flex'
                    />
                    {
                        step === 0 &&
                        <Form {...form}>
                            <form>
                                <FormField
                                    name={'orderId'}
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem className='select-profile'>
                                            <FormLabel required>
                                                <Entity namespace={nsSimCard} entity='modal.add.labels.order' />
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    autoComplete='off'
                                                    value={field.value}
                                                    onChange={(e) =>
                                                        field.onChange(e.target.value)
                                                    }
                                                    namespace={nsSimCard}
                                                    entity={'modal.add.placeholder.order'}
                                                    className='h-8'
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </form>
                        </Form>
                    }
                    {
                        step === 1 &&
                        <Entity entity={'modal.add.text.confirmation'} namespace={nsSimCard} data={{ orderId: form.getValues('orderId') }} />
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    stepsNumber={STEPS_NUMBER}
                    setStep={setStep}
                    onValidate={() => {
                        submitAddSimCardsFromOrderId.mutate({
                            orderId: Number(form.getValues('orderId')),
                            organizationId: rootOrganization.uuid
                        });
                    }}
                    disableValidation={disabledNext}
                    isLoading={submitAddSimCardsFromOrderId.isLoading}
                />
            </ModalContent>
        </Modal>
    );
}

CustomPayModalAddSimCards.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};

export default CustomPayModalAddSimCards;